import { useEffect, useMemo, useRef, useState } from "react";
import { Calendar16Regular, ChevronLeft16Regular, ChevronRight16Regular } from "@fluentui/react-icons";

const pad = (n: number) => String(n).padStart(2, "0");
const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** The order in which a locale writes day, month and year ("d m y" for French, "m d y" for US English, "y m d" for Japanese). */
function orderOf(locale: string): ("day" | "month" | "year")[] {
  return new Intl.DateTimeFormat(locale, { year: "numeric", month: "2-digit", day: "2-digit" })
    .formatToParts(new Date(2000, 10, 22))
    .map((p) => p.type)
    .filter((t): t is "day" | "month" | "year" => t === "day" || t === "month" || t === "year");
}

const valid = (y: number, m: number, d: number): boolean => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
};

/** `2026-10-19` as the person writes dates ("19/10/2026" in French), empty when it is not a date. */
export function formatDateInput(iso: string, locale: string): string {
  if (!ISO.test(iso)) return "";
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  if (!valid(y, m, d)) return "";
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Reads what the person typed, in the order of their language or as `yyyy-mm-dd`; empty while it is incomplete or not a date. */
export function parseDateInput(text: string, locale: string): string {
  const t = text.trim();
  if (ISO.test(t)) {
    const [y, m, d] = t.split("-").map(Number) as [number, number, number];
    return valid(y, m, d) ? t : "";
  }
  const parts = t.split(/\D+/).filter(Boolean);
  if (parts.length !== 3) return "";
  const v: Record<string, number> = {};
  const order = orderOf(locale);
  order.forEach((k, i) => (v[k] = Number(parts[i])));
  if (parts[order.indexOf("year")]!.length < 4) return "";
  return valid(v["year"]!, v["month"]!, v["day"]!)
    ? `${v["year"]}-${pad(v["month"]!)}-${pad(v["day"]!)}`
    : "";
}

function firstDayOfWeek(locale: string): number {
  try {
    const l = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const info = l.getWeekInfo?.() ?? l.weekInfo;
    if (info) return info.firstDay % 7; // 1 = Monday … 7 = Sunday -> 0..6 with Sunday = 0
  } catch {
    /* fall through */
  }
  return locale.toLowerCase().startsWith("en") ? 0 : 1;
}

export interface DateInputProps {
  id?: string;
  value: string;
  /** called with `{ target: { value } }` (an ISO date, or "" while the text is not a date), like an input */
  onChange: (e: { target: { value: string } }) => void;
  /** the person's language: it decides how the date is written and the names in the calendar, not the browser's */
  locale: string;
  min?: string;
  max?: string;
  disabled?: boolean;
  openLabel?: string;
  previousLabel?: string;
  nextLabel?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
}

/**
 * Date field in the person's language (the native date input follows the browser, not Teams): the date is typed as the language
 * writes it, or picked in a calendar whose month and day names come from the same language.
 */
export function DateInput(p: DateInputProps) {
  const { value, onChange, locale, min, max } = p;
  const [text, setText] = useState(() => formatDateInput(value, locale));
  const [focused, setFocused] = useState(false);
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!focused) setText(formatDateInput(value, locale));
  }, [value, locale, focused]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  return (
    <div
      className="gp-date"
      ref={root}
      onKeyDown={(e) => e.key === "Escape" && open && (e.stopPropagation(), setOpen(false))}
    >
      <div className="gp-date-box">
        <input
          id={p.id}
          type="text"
          inputMode="numeric"
          className="gp-input gp-date-text"
          value={text}
          placeholder={formatDateInput(new Date().toISOString().slice(0, 10), locale)}
          disabled={p.disabled}
          aria-describedby={p["aria-describedby"]}
          aria-invalid={p["aria-invalid"]}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setText(formatDateInput(value, locale));
          }}
          onChange={(e) => {
            setText(e.target.value);
            onChange({ target: { value: parseDateInput(e.target.value, locale) } });
          }}
        />
        <button
          type="button"
          className="gp-date-open"
          aria-label={p.openLabel ?? "Calendar"}
          aria-expanded={open}
          disabled={p.disabled}
          onClick={() => setOpen((o) => !o)}
        >
          <Calendar16Regular aria-hidden />
        </button>
      </div>
      {open ? (
        <Calendar
          locale={locale}
          value={value}
          {...(min ? { min } : {})}
          {...(max ? { max } : {})}
          previousLabel={p.previousLabel ?? "Previous month"}
          nextLabel={p.nextLabel ?? "Next month"}
          onPick={(iso) => {
            onChange({ target: { value: iso } });
            setText(formatDateInput(iso, locale));
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

function Calendar({
  locale,
  value,
  min,
  max,
  previousLabel,
  nextLabel,
  onPick,
}: {
  locale: string;
  value: string;
  min?: string;
  max?: string;
  previousLabel: string;
  nextLabel: string;
  onPick: (iso: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const start = ISO.test(value) ? value : today;
  const [shown, setShown] = useState({ y: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) });
  const first = firstDayOfWeek(locale);
  const box = useRef<HTMLDivElement>(null);
  // inside a scrolling dialog the calendar opens below the field: bring it into view
  useEffect(() => {
    box.current?.scrollIntoView?.({ block: "nearest" });
  }, []);
  const days = useMemo(() => {
    const lead = (new Date(Date.UTC(shown.y, shown.m - 1, 1)).getUTCDay() - first + 7) % 7;
    const count = new Date(Date.UTC(shown.y, shown.m, 0)).getUTCDate();
    return [...Array<null>(lead).fill(null), ...Array.from({ length: count }, (_, i) => i + 1)];
  }, [shown, first]);
  const names = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) =>
        new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(
          new Date(Date.UTC(2024, 0, 7 + ((first + i) % 7))), // 2024-01-07 is a Sunday
        ),
      ),
    [locale, first],
  );
  const title = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(
    new Date(Date.UTC(shown.y, shown.m - 1, 1)),
  );
  const move = (delta: number) =>
    setShown(({ y, m }) => {
      const t = new Date(Date.UTC(y, m - 1 + delta, 1));
      return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1 };
    });
  return (
    <div className="gp-cal" role="dialog" aria-label={title} ref={box}>
      <div className="gp-cal-head">
        <button type="button" className="gp-date-open" aria-label={previousLabel} onClick={() => move(-1)}>
          <ChevronLeft16Regular aria-hidden />
        </button>
        <span className="t-body-strong" aria-live="polite">
          {title}
        </span>
        <button type="button" className="gp-date-open" aria-label={nextLabel} onClick={() => move(1)}>
          <ChevronRight16Regular aria-hidden />
        </button>
      </div>
      <div className="gp-cal-grid" role="grid">
        {names.map((n) => (
          <span key={n} className="gp-cal-dow t-caption" aria-hidden>
            {n}
          </span>
        ))}
        {days.map((d, i) => {
          if (d === null) return <span key={`b${i}`} />;
          const iso = `${shown.y}-${pad(shown.m)}-${pad(d)}`;
          const off = (min !== undefined && iso < min) || (max !== undefined && iso > max);
          return (
            <button
              key={iso}
              type="button"
              className="gp-cal-day"
              disabled={off}
              aria-pressed={iso === value}
              data-today={iso === today ? "true" : undefined}
              onClick={() => onPick(iso)}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}
